import { useEffect, useMemo, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { faCopy } from '@fortawesome/pro-light-svg-icons'
import { Alert, Button, Dialog, Form } from '@/ui'
import {
  getFieldNames,
  makeReqStringSchema,
  makeStringArraySchema,
} from '@/base'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  BULK_UPDATE_USER_INCOME_MONTHS_KEY,
  bulkUpdateUserIncomeMonths,
  fetchUserIncomePlans,
  selectUserIncomePlanById,
  selectUserIncomePlans,
} from '@/features/income-planning/incomePlanning.slice'
import { useReselector } from '@/utils/sharedHooks'
import { useSelectedMonthOptions } from '@/features/income-planning/utils'
import { PlanInfoCard } from '@/features/income-planning/PlanInfoCard'
import { getFetchError } from '@/reducers/fetch'

export const ApplyPlanDialog = ({
  selectedMonths,
  setSelectedMonths,
  setUpdatedMonths,
  selectedYear,
}: {
  selectedMonths: number[]
  setSelectedMonths: (months: number[]) => void
  setUpdatedMonths: (months: number[]) => void
  selectedYear: number
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const error = useReselector(getFetchError, BULK_UPDATE_USER_INCOME_MONTHS_KEY)

  useEffect(() => {
    dispatch(fetchUserIncomePlans())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      selectedMonths: selectedMonths.map((month) => `${selectedYear}-${month}`),
      planId: null,
    },
    enableReinitialize: true,
    onSubmit: async ({ planId, selectedMonths }) => {
      if (!planId) {
        return
      }

      const updatedMonths = await dispatch(
        bulkUpdateUserIncomeMonths({
          planId: Number(planId),
          months: selectedMonths.map((month) => ({
            month: Number(month.split('-')[1]),
            year: Number(month.split('-')[0]),
          })),
        })
      )

      if (!updatedMonths) {
        return
      }

      setUpdatedMonths(updatedMonths.map((m) => m.id))
      setSelectedMonths([])
      setOpen(false)
    },
  })

  const incomePlans = useReselector(selectUserIncomePlans)
  const selectedMonthOptions = useSelectedMonthOptions()

  const selectedPlanOptions = useMemo(
    () =>
      Object.values(incomePlans).map((plan) => ({
        value: plan.id,
        text: plan.planName,
      })),
    [incomePlans]
  )

  const { submitForm, values } = formik
  const fieldNames = getFieldNames(formik)

  const selectedPlan = useReselector(selectUserIncomePlanById, values.planId)

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <FormikProvider value={formik}>
        <Dialog.Trigger asChild>
          <Button
            iconLeft={faCopy}
            variant="secondary"
            onClick={() => setOpen(true)}
          >
            {selectedMonths.length
              ? `Apply Plan to ${selectedMonths.length} month${selectedMonths.length > 1 ? 's' : ''}`
              : 'Apply Plan'}
          </Button>
        </Dialog.Trigger>
        <Dialog.Content>
          <Dialog.Title>Apply plan</Dialog.Title>
          <Dialog.Description>
            Select which plan you’d like to apply to the selected month(s).
          </Dialog.Description>
          {error && (
            <Alert variant="error" title={error.message} className="mt-4" />
          )}
          <Alert
            title="Applying a plan to the selected months will overwrite their existing values with new values."
            className="mt-6"
          />
          <div className="mt-6">
            <Form.Multiselect
              label="Apply to"
              options={selectedMonthOptions}
              name={fieldNames.selectedMonths}
              schema={makeStringArraySchema()}
            />
          </div>
          <div className="mt-6">
            <Form.Select
              label="Select"
              options={selectedPlanOptions}
              name={fieldNames.planId}
              schema={makeReqStringSchema()}
            />
          </div>
          {selectedPlan && (
            <PlanInfoCard showComparePlan={false} sentPlan={selectedPlan} />
          )}
          <Dialog.Footer className="mt-6">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitForm}>Apply</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </FormikProvider>
    </Dialog>
  )
}
